import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Mail, Phone, Globe, Linkedin, User, Building } from 'lucide-react'
import toast from 'react-hot-toast'

interface ContactInfoProps {
  vendor: {
    name: string
    email: string
    phone?: string
    website?: string
    linkedinUrl?: string
    company?: string
  }
  supplier: {
    name: string
    email: string
    phone?: string
    website?: string
    linkedinUrl?: string
    company?: string
  }
}

export function ContactInfo({ vendor, supplier }: ContactInfoProps) {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!')
  }

  return (
    <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-300">
          <User className="h-5 w-5" />
          Mutual Contact Information
        </CardTitle>
        <CardDescription className="text-green-600 dark:text-green-400">
          Contact details are now shared between vendor and supplier
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Vendor Information */}
        <div className="space-y-3">
          <h4 className="font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
            <Building className="h-4 w-4" />
            Vendor: {vendor.name}
          </h4>
          <div className="space-y-2 pl-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{vendor.email}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(vendor.email)}
                className="h-6 px-2 text-xs"
              >
                Copy
              </Button>
            </div>
            {vendor.phone && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{vendor.phone}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(vendor.phone!)}
                  className="h-6 px-2 text-xs"
                >
                  Copy
                </Button>
              </div>
            )}
            {vendor.website && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={vendor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {vendor.website}
                  </a>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(vendor.website!)}
                  className="h-6 px-2 text-xs"
                >
                  Copy
                </Button>
              </div>
            )}
            {vendor.linkedinUrl && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Linkedin className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={vendor.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                  >
                    LinkedIn Profile
                  </a>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(vendor.linkedinUrl!)}
                  className="h-6 px-2 text-xs"
                >
                  Copy
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Supplier Information */}
        <div className="space-y-3">
          <h4 className="font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
            <Building className="h-4 w-4" />
            Supplier: {supplier.name}
          </h4>
          <div className="space-y-2 pl-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{supplier.email}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(supplier.email)}
                className="h-6 px-2 text-xs"
              >
                Copy
              </Button>
            </div>
            {supplier.phone && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{supplier.phone}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(supplier.phone!)}
                  className="h-6 px-2 text-xs"
                >
                  Copy
                </Button>
              </div>
            )}
            {supplier.website && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={supplier.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {supplier.website}
                  </a>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(supplier.website!)}
                  className="h-6 px-2 text-xs"
                >
                  Copy
                </Button>
              </div>
            )}
            {supplier.linkedinUrl && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Linkedin className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={supplier.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                  >
                    LinkedIn Profile
                  </a>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(supplier.linkedinUrl!)}
                  className="h-6 px-2 text-xs"
                >
                  Copy
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
